import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonCTA from '@/components/base/ButtonCTA';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import PriceTable from '@/components/feature/PriceTable';
import FAQAccordion from '@/components/feature/FAQAccordion';
import CTABlock from '@/components/feature/CTABlock';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { REASSURANCE_ITEMS } from '@/mocks/homeData';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

const PRICING_ROWS = [
  { service: 'Traitement jardin particulier (audit + gîtes + zones de repos)', price: '120 € – 300 €' },
  { service: 'Forfait saison des pluies (passages réguliers juin-novembre)', price: 'Sur devis' },
  { service: 'Hôtels, restaurants de plage, gîtes', price: 'Sur devis, plan annuel' },
];

const FAQ_ITEMS = [
  {
    question: 'Le traitement protège-t-il de la dengue ?',
    answer: 'Il réduit fortement la population d\'Aedes sur votre terrain, donc l\'exposition aux piqûres — c\'est LA mesure individuelle efficace en complément des gestes officiels (élimination des eaux stagnantes, répulsifs). La lutte anti-vectorielle de masse reste pilotée par les autorités sanitaires.',
  },
  {
    question: 'Combien de temps ça dure ?',
    answer: 'Quelques semaines à plusieurs mois selon la saison et la pluie. En saison des pluies, le forfait à passages réguliers est la seule solution vraiment stable.',
  },
  {
    question: 'C\'est dangereux pour les abeilles, le potager, le bassin ?',
    answer: 'Nos techniciens travaillent en pulvérisation ciblée (zones de repos, pas de traitement en floraison), avec des larvicides biologiques (Bti) pour les points d\'eau. Dites-nous ce que vous avez au jardin : le protocole s\'adapte.',
  },
  {
    question: 'Pourquoi les moustiques reviennent après la pluie ?',
    answer: 'Chaque pluie remplit de nouveaux gîtes et fait éclore des œufs pondus à sec (l\'Aedes pond au-dessus de la ligne d\'eau). D\'où l\'importance du volet gîtes et de l\'entretien.',
  },
  {
    question: 'Traitez-vous pendant une épidémie de dengue ?',
    answer: 'Oui, avec des délais d\'intervention prioritaires pour les foyers avec personnes fragiles et les hébergeurs.',
  },
];

export default function Demoustication() {
  useDocumentHead({
    title: 'Démoustication Guadeloupe : jardins, terrasses, hôtels',
    description: 'Moustiques qui rendent votre jardin invivable ? Traitement des gîtes larvaires et zones de repos en Guadeloupe. Particuliers et hôtels. Devis gratuit.',
    jsonLd: [service({ name: 'Démoustication en Guadeloupe', serviceType: 'Démoustication', path: '/demoustication-guadeloupe', description: 'Traitement des gîtes larvaires et zones de repos des moustiques en Guadeloupe.' }), faqPage(FAQ_ITEMS), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Moustiques', path: '/demoustication-guadeloupe' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Moustiques'}]} />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/demoustication-1.webp"
            alt="Technicien en tenue de protection pulvérisant un traitement anti-moustiques dans un jardin tropical en Guadeloupe"
            className="w-full h-full object-cover object-top"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Démoustication en Guadeloupe : reprenez possession de votre jardin
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Impossible de dîner en terrasse, enfants piqués dès 17 h, et l&rsquo;inquiétude qui monte à chaque épidémie de dengue : en Guadeloupe, le moustique — et surtout l&rsquo;Aedes aegypti, vecteur de la dengue, du chikungunya et du Zika — n&rsquo;est pas qu&rsquo;une nuisance, c&rsquo;est un enjeu de santé. La lutte de masse relève des autorités sanitaires, mais votre terrain, lui, peut être traité : nos techniciens éliminent les gîtes larvaires et traitent les zones de repos des moustiques pour faire chuter durablement leur présence chez vous.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <ButtonCTA>Devis gratuit</ButtonCTA>
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <ReassuranceBar items={REASSURANCE_ITEMS} />

      {/* POURQUOI VOTRE JARDIN EST INFESTÉ */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                  Pourquoi votre jardin est infesté
                </h2>
                <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                  <p>
                    <strong>L&rsquo;Aedes aegypti naît chez vous ou chez vos voisins immédiats</strong> : il vole peu (moins de 100 m) et pond dans la moindre eau stagnante — soucoupes, gouttières bouchées, pneus, seaux, bâches, bromélias, fûts de récupération. Un bouchon de bouteille d&rsquo;eau suffit à une ponte.
                  </p>
                  <p>
                    Après chaque pluie, chaque gîte redémarre. C&rsquo;est pourquoi tuer les adultes sans traiter les gîtes ne sert à rien plus de quelques jours.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="w-full h-72 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src="/images/demoustication-2.webp"
                    alt="Inspection des zones de reproduction de moustiques dans un jardin tropical en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* NOTRE TRAITEMENT */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10 text-center">
              Notre traitement
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-10">
              <div>
                <p className="text-foreground-600 text-base leading-relaxed">
                  Audit complet du terrain, élimination des gîtes larvaires, et pulvérisation professionnelle des zones de repos des moustiques adultes. En quelques jours, la différence est nette — et elle dure. Pour les terrains exposés, nos forfaits saisonniers maintiennent la protection toute l&rsquo;année.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-64 md:h-72 rounded-xl overflow-hidden">
                  <img
                    src="/images/demoustication-3.webp"
                    alt="Application d'un traitement adulticide anti-moustiques avec nébulisateur dans un jardin tropical en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 border border-background-200/70">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-secondary-100">
                    <span className="font-heading font-black text-secondary-500 text-lg">1</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground-950 text-base mb-2">
                      Audit du terrain
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      Inventaire complet des gîtes larvaires (on en trouve toujours plus que le client n&rsquo;imagine), zones de repos des adultes (haies denses, dessous de feuilles, zones ombragées humides).
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-background-200/70">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-secondary-100">
                    <span className="font-heading font-black text-secondary-500 text-lg">2</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground-950 text-base mb-2">
                      Élimination et traitement des gîtes
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      Suppression, retournement, ou traitement larvicide des points d&rsquo;eau non supprimables (fûts, regards).
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-background-200/70">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-secondary-100">
                    <span className="font-heading font-black text-secondary-500 text-lg">3</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground-950 text-base mb-2">
                      Traitement des zones de repos
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      Pulvérisation professionnelle rémanente sur les végétaux refuges et zones stratégiques — là où les moustiques passent 90 % de leur temps.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-background-200/70">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-secondary-100">
                    <span className="font-heading font-black text-secondary-500 text-lg">4</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-foreground-950 text-base mb-2">
                      Plan d&rsquo;entretien
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      Conseils gîte par gîte, et forfaits saisonniers pour les terrains exposés (ravines, mares voisines) et les professionnels.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-3xl mx-auto mt-6">
              <div className="bg-secondary-100/50 rounded-xl p-5 border border-secondary-200/50 text-center">
                <p className="text-foreground-700 text-sm leading-relaxed">
                  <strong>Effet net en quelques jours</strong>, protection de plusieurs semaines à plusieurs mois selon la formule et la saison.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* TARIFS */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Nos tarifs de démoustication
              </h2>
            </div>
            <div className="max-w-2xl mx-auto">
              <PriceTable rows={PRICING_ROWS} />
              <div className="text-center mt-6">
                <Link
                  to="/tarifs"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold hover:text-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voir tous les tarifs
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* HÔTELS ET RESTAURANTS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                Hôtels et restaurants : la terrasse sans moustiques est un argument commercial
              </h2>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center shrink-0 rounded-full bg-mango-500/20 mt-1">
                  <i className="ri-star-line text-mango-500 text-lg w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-foreground-600 text-base leading-relaxed">
                  Vos clients dînent dehors — vos avis Google aussi. Nos plans annuels pour hébergeurs et restaurants combinent traitement larvicide des points d&rsquo;eau, pulvérisations programmées hors présence clients, et interventions renforcées en période d&rsquo;épidémie.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* FAQ */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Questions fréquentes sur la démoustication
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion items={FAQ_ITEMS} />
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <CTABlock
        title="Vos soirées en terrasse valent mieux que ça."
      />

      {/* MAILLAGE INTERNE */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/desinsectisation-guadeloupe"
              className="block bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Tous nos traitements désinsectisation
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Quel que soit l&rsquo;insecte, on a le traitement
              </span>
            </Link>
            <Link
              to="/tarifs"
              className="block bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Voir tous nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="demoustication" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}